import { cn, formatTime } from '$app/utils'
import CopyButton from '$components/CopyButon/v2'
import { TData_OTP } from '$services/api'
import { flexColumn, flexRow, ready } from '$styles/common.css'
import { Button, Spinner } from '@genie-fintech/ui/components'
import { trueOrUndefined } from '@genie-fintech/ui/functions'
import { useIsMounted } from '@genie-fintech/ui/hooks'
import { Icon } from '@genie-fintech/ui/icons'
import { Namespaces } from '@genie-fintech/ui/icons/types'
import { status, StatusVariants } from '@genie-fintech/ui/style/element'
import { themeVars } from '@genie-fintech/ui/style/theme'
import { markedDefaultKey } from '@genie-fintech/ui/style/theme/colors/functions'
import {
  footnote,
  title as titleStyle
} from '@genie-fintech/ui/style/typography'
import { useCountDown } from 'ahooks'
import { Clock } from 'lucide-react'
import { useMemo } from 'react'
import { grid, referenceCodeText } from './styles.css'

const { colors } = themeVars

const OTPData = ({
  data,
  callbackFn
}: {
  data: TData_OTP
  callbackFn: VoidFunction
}) => {
  const isMounted = useIsMounted()

  const isValidOTP = data.code && data.expires_in && !data.is_verified

  const isOTPExpired = data.code && !data.expires_in && !data.is_verified

  const isOTPNotFound = !!data.message || data.is_verified

  const targetDate = useMemo(() => {
    if (isOTPNotFound) return undefined

    return data.expires_in ? Date.now() + data.expires_in * 1000 : undefined
  }, [isOTPNotFound, data.expires_in])

  const [, { seconds, minutes }] = useCountDown({
    targetDate,
    onEnd: () => {
      callbackFn()
    }
  })

  const statusKind: StatusVariants['kind'] = (() => {
    if (isValidOTP) return 'success'
    if (isOTPExpired) return 'danger'
    return 'grey'
  })()

  const iconName: Namespaces = (() => {
    if (isValidOTP) return 'Check'
    return 'Warning'
  })()

  const statusText = (() => {
    if (isValidOTP) return 'Requested OTP code is here!'
    if (isOTPExpired) return 'Requested OTP code is expired!'
    return 'No requested OTP is found!'
  })()

  return (
    <article
      className={cn('flex flex-col gap-2', ready)}
      data-ready={trueOrUndefined(isMounted)}
    >
      {!isOTPNotFound && (
        <>
          <article
            className="flex p-4 items-center justify-between rounded border"
            style={{ borderColor: colors.neutral[10] }}
          >
            <Button
              styleVariants={{ type: 'text', kind: 'neutral', size: 'small' }}
              className="pointer-events-none"
            >
              <Clock size={16} />
              <span>
                {formatTime(minutes)}:{formatTime(seconds)} min
              </span>
            </Button>

            <article className="inline-flex gap-3">
              <h3 className={titleStyle.one}>{data.code}</h3>
              <CopyButton size={20} color="primary.default" value={data.code} />
            </article>
          </article>

          <p className={referenceCodeText.base}>
            <span className={referenceCodeText.label}>Reference Code: </span>(
            <span className={referenceCodeText.value}>
              {data.reference_code}
            </span>
            )
          </p>
        </>
      )}

      <article className="flex justify-end">
        <span
          className={status({
            kind: statusKind,
            style: 'rounded',
            type: 'faint',
            material: 'icon'
          })}
        >
          <Icon namespace={iconName} />
          {statusText}
        </span>
      </article>
    </article>
  )
}

type OTPSettingProps = {
  otpData?: TData_OTP
  loading: boolean
  onGetOTP: VoidFunction
  title: string
  disabled?: boolean
}

const OTPSetting = ({
  otpData,
  onGetOTP,
  loading,
  title,
  disabled: forceDisabled
}: OTPSettingProps) => {
  return (
    <article className={grid}>
      <article className={flexColumn} style={{ gap: 12 }}>
        <article className={flexColumn} style={{ gap: 4 }}>
          <h4 className={titleStyle.two} style={{ textTransform: 'uppercase' }}>
            {title}
          </h4>
          <p className={footnote.one} style={{ color: colors.neutral[60] }}>
            This OTP can use only once on requested situation and it has time
            interval showing accordingly.
          </p>
        </article>

        <p
          className={footnote.three}
          style={{ color: colors.warning[markedDefaultKey] }}
        >
          To protect the owner account, never share this code with anyone. It is
          for the owner's use only.
        </p>
      </article>

      <article className={flexColumn} style={{ gap: 16 }}>
        {otpData && !loading && (
          <OTPData data={otpData} callbackFn={onGetOTP} />
        )}

        <article className={flexRow} style={{ justifyContent: 'flex-end' }}>
          <Button
            styleVariants={{ size: 'small' }}
            disabled={loading || forceDisabled}
            onClick={onGetOTP}
          >
            {loading && <Spinner />}
            Get Emergency OTP
          </Button>
        </article>
      </article>
    </article>
  )
}

export default OTPSetting
